import React from 'react';
import { useFinoraTheme } from '@/contexts/ThemeContext';
import type { AmbientAnimation } from '@/lib/theme-definitions';

/* ─── Animation Components ─────────────────────────────── */

function LightRays() {
  return (
    <>
      <div
        className="absolute -top-1/2 -left-1/4 w-[150%] h-[200%] ambient-anim-drift"
        style={{ opacity: 0.22 }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/10 to-transparent rotate-12" />
      </div>
      <div
        className="absolute -top-1/4 -right-1/4 w-[120%] h-[180%] ambient-anim-drift-reverse"
        style={{ opacity: 0.15 }}
      >
        <div className="absolute inset-0 bg-gradient-to-bl from-accent/30 via-accent/8 to-transparent -rotate-6" />
      </div>
      <div
        className="absolute top-1/4 left-1/3 w-[60%] h-[60%] rounded-full ambient-anim-pulse blur-3xl"
        style={{ opacity: 0.12, background: 'radial-gradient(circle, hsl(var(--primary) / 0.3), transparent 70%)' }}
      />
    </>
  );
}

function FlowingLeaves() {
  return (
    <>
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={`leaf-${i}`}
          className="absolute ambient-anim-leaf-fall"
          style={{
            left: `${(i * 8.5 + 3) % 100}%`,
            top: `-8%`,
            animationDelay: `${i * -2.5}s`,
            animationDuration: `${18 + (i % 4) * 5}s`,
            opacity: 0.08 + (i % 3) * 0.03,
          }}
        >
          <div
            className="ambient-anim-leaf-spin"
            style={{
              width: `${8 + (i % 3) * 4}px`,
              height: `${12 + (i % 3) * 5}px`,
              backgroundColor: i % 2 === 0
                ? `hsl(var(--primary) / 0.3)`
                : `hsl(var(--accent) / 0.25)`,
              borderRadius: '50% 50% 50% 0',
              transform: `rotate(${45 + (i % 4) * 20}deg)`,
              animationDelay: `${i * -1.8}s`,
              animationDuration: `${6 + (i % 3) * 2}s`,
            }}
          />
        </div>
      ))}
      <div className="absolute inset-0 ambient-anim-drift" style={{ opacity: 0.06 }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-accent/10" />
      </div>
    </>
  );
}

function Ripple() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="absolute rounded-full ambient-anim-ripple"
          style={{
            width: `${35 + i * 20}%`,
            height: `${35 + i * 20}%`,
            border: `1px solid hsl(var(--primary) / ${0.10 - i * 0.015})`,
            animationDelay: `${i * -5}s`,
            animationDuration: `${24 + i * 4}s`,
          }}
        />
      ))}
      <div
        className="absolute w-[40%] h-[40%] rounded-full ambient-anim-pulse blur-3xl"
        style={{ opacity: 0.06, background: 'radial-gradient(circle, hsl(var(--primary) / 0.15), transparent 70%)' }}
      />
    </div>
  );
}

function TwirlingWind() {
  return (
    <>
      {/* Sweeping wind streaks */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={`streak-${i}`}
          className="absolute ambient-anim-wind-drift"
          style={{
            left: `${-10 + (i * 14) % 100}%`,
            top: `${(i * 12 + 8) % 85}%`,
            width: `${120 + (i % 3) * 60}px`,
            height: `${1.5 + (i % 2)}px`,
            background: `linear-gradient(90deg, transparent, hsl(var(--primary) / ${0.12 + (i % 3) * 0.04}), transparent)`,
            borderRadius: '50%',
            animationDelay: `${i * -2.5}s`,
            animationDuration: `${12 + (i % 4) * 3}s`,
          }}
        />
      ))}
      {/* Rotating swirl arcs */}
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={`swirl-${i}`}
          className="absolute ambient-anim-wind-swirl"
          style={{
            left: `${(i * 20 + 8) % 90}%`,
            top: `${(i * 18 + 12) % 75}%`,
            width: `${80 + (i % 3) * 40}px`,
            height: `${80 + (i % 3) * 40}px`,
            animationDelay: `${i * -5}s`,
            animationDuration: `${22 + (i % 3) * 6}s`,
            opacity: 0.15 + (i % 3) * 0.05,
          }}
        >
          <div
            className="w-full h-full rounded-full"
            style={{
              border: `1.5px solid hsl(var(--primary) / ${0.18 - (i % 3) * 0.03})`,
              borderTopColor: 'transparent',
              borderRightColor: 'transparent',
            }}
          />
        </div>
      ))}
      {/* Drifting dust particles */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={`dust-${i}`}
          className="absolute rounded-full ambient-anim-wind-drift"
          style={{
            width: `${3 + (i % 3)}px`,
            height: `${3 + (i % 3)}px`,
            backgroundColor: `hsl(var(--primary) / ${0.18 + (i % 3) * 0.06})`,
            left: `${(i * 8.5 + 2) % 100}%`,
            top: `${(i * 8 + 5) % 100}%`,
            animationDelay: `${i * -1.8}s`,
            animationDuration: `${10 + (i % 4) * 3}s`,
          }}
        />
      ))}
      {/* Subtle ground haze */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 ambient-anim-drift" style={{ opacity: 0.08 }}>
        <div className="w-full h-full bg-gradient-to-t from-accent/15 via-accent/5 to-transparent" />
      </div>
    </>
  );
}

function Stars() {
  return (
    <>
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full ambient-anim-twinkle"
          style={{
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            backgroundColor: `hsl(var(--primary-foreground) / ${0.5 + (i % 4) * 0.12})`,
            left: `${(i * 3.4 + 1.5) % 100}%`,
            top: `${(i * 5.3 + 2) % 100}%`,
            animationDelay: `${i * -0.9}s`,
            animationDuration: `${5 + (i % 5) * 2}s`,
          }}
        />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={`d${i}`}
          className="absolute rounded-full ambient-anim-drift"
          style={{
            width: `${3 + (i % 2)}px`,
            height: `${3 + (i % 2)}px`,
            backgroundColor: `hsl(var(--accent) / ${0.4 + (i % 3) * 0.12})`,
            left: `${(i * 12.5 + 4) % 100}%`,
            top: `${(i * 11.2 + 8) % 100}%`,
            animationDelay: `${i * -3.5}s`,
            animationDuration: `${25 + i * 4}s`,
          }}
        />
      ))}
      <div
        className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full ambient-anim-pulse blur-3xl"
        style={{ opacity: 0.08, background: 'radial-gradient(circle, hsl(var(--primary) / 0.35), hsl(var(--accent) / 0.15) 50%, transparent 70%)' }}
      />
    </>
  );
}

function GradientWave() {
  return (
    <>
      <div className="absolute inset-0 ambient-anim-wave" style={{ opacity: 0.2 }}>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/35 via-accent/20 to-primary/35" style={{ backgroundSize: '200% 100%' }} />
      </div>
      <div className="absolute inset-0 ambient-anim-wave-reverse" style={{ opacity: 0.14 }}>
        <div className="absolute inset-0 bg-gradient-to-br from-accent/30 via-transparent to-primary/25" style={{ backgroundSize: '200% 200%' }} />
      </div>
      <div
        className="absolute top-1/3 left-1/4 w-1/2 h-1/3 rounded-full ambient-anim-drift blur-3xl"
        style={{ opacity: 0.1, background: 'radial-gradient(circle, hsl(var(--accent) / 0.25), transparent 70%)' }}
      />
    </>
  );
}

function ZenRipple() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg className="absolute w-[140%] h-[140%]" viewBox="0 0 400 400">
        {Array.from({ length: 8 }).map((_, i) => (
          <circle
            key={i}
            cx="200"
            cy="200"
            r={30 + i * 25}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="1"
            className="ambient-anim-zen-ripple"
            style={{
              animationDelay: `${i * -2.5}s`,
              animationDuration: `${16 + i * 2}s`,
            }}
          />
        ))}
      </svg>
      <div
        className="absolute w-4 h-4 rounded-full ambient-anim-pulse"
        style={{ backgroundColor: 'hsl(var(--primary) / 0.25)' }}
      />
    </div>
  );
}

function Canopy() {
  return (
    <>
      <div
        className="absolute -top-1/2 left-[15%] w-[40%] h-[200%] ambient-anim-drift"
        style={{ opacity: 0.18, transform: 'rotate(15deg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-accent/40 via-accent/12 to-transparent" />
      </div>
      <div
        className="absolute -top-1/4 right-[20%] w-[30%] h-[180%] ambient-anim-drift-reverse"
        style={{ opacity: 0.14, transform: 'rotate(-8deg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-accent/30 via-primary/10 to-transparent" />
      </div>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div
          key={`spot-${i}`}
          className="absolute rounded-full ambient-anim-pulse blur-xl"
          style={{
            width: `${35 + i * 12}px`,
            height: `${35 + i * 12}px`,
            backgroundColor: `hsl(var(--accent) / ${0.15 + (i % 3) * 0.04})`,
            left: `${15 + i * 14}%`,
            top: `${10 + (i * 15) % 70}%`,
            animationDelay: `${i * -3}s`,
            animationDuration: `${18 + i * 3}s`,
          }}
        />
      ))}
      {[0, 1, 2, 3, 4].map(i => (
        <div
          key={`leaf-${i}`}
          className="absolute ambient-anim-float"
          style={{
            left: `${8 + i * 20}%`,
            top: `${5 + (i * 18) % 80}%`,
            animationDelay: `${i * -4}s`,
            animationDuration: `${22 + i * 3}s`,
            opacity: 0.2,
          }}
        >
          <div className="w-4 h-6 rotate-45" style={{ backgroundColor: 'hsl(var(--primary) / 0.35)', borderRadius: '50% 50% 50% 0' }} />
        </div>
      ))}
    </>
  );
}

function Particles() {
  return (
    <>
      {Array.from({ length: 25 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full ambient-anim-float"
          style={{
            width: `${2 + (i % 3) * 0.8}px`,
            height: `${2 + (i % 3) * 0.8}px`,
            backgroundColor: i % 3 === 0
              ? `hsl(var(--accent) / ${0.4 + (i % 4) * 0.1})`
              : `hsl(var(--primary) / ${0.3 + (i % 4) * 0.08})`,
            left: `${(i * 4.2 + 2) % 100}%`,
            top: `${(i * 6.6 + 4) % 100}%`,
            animationDelay: `${i * -1.4}s`,
            animationDuration: `${22 + (i % 6) * 3}s`,
          }}
        />
      ))}
      <div
        className="absolute top-[20%] left-[15%] w-[35%] h-[35%] rounded-full ambient-anim-drift blur-3xl"
        style={{ opacity: 0.1, background: 'radial-gradient(circle, hsl(var(--primary) / 0.3), transparent 70%)' }}
      />
      <div
        className="absolute bottom-[25%] right-[20%] w-[30%] h-[30%] rounded-full ambient-anim-drift-reverse blur-3xl"
        style={{ opacity: 0.07, background: 'radial-gradient(circle, hsl(var(--accent) / 0.25), transparent 70%)' }}
      />
    </>
  );
}

/* ─── Animation Registry ────────────────────────────────── */

const ANIMATION_MAP: Record<AmbientAnimation, React.FC> = {
  'light-rays': LightRays,
  'flowing-leaves': FlowingLeaves,
  ripple: Ripple,
  'twirling-wind': TwirlingWind,
  stars: Stars,
  'gradient-wave': GradientWave,
  'zen-ripple': ZenRipple,
  canopy: Canopy,
  particles: Particles,
};

/* ─── Main Component ────────────────────────────────────── */

export function AmbientBackground() {
  const { themeDefinition, reduceMotion } = useFinoraTheme();
  const animationType = themeDefinition?.animation;
  const AnimComponent = animationType ? ANIMATION_MAP[animationType] : null;

  return (
    <div
      id="ambient-background"
      className="fixed inset-0 z-0 pointer-events-none overflow-hidden ambient-layer"
      aria-hidden="true"
    >
      {/* Base background */}
      <div className="absolute inset-0 bg-background" />
      {/* Animated layer */}
      {!reduceMotion && AnimComponent && <AnimComponent />}
    </div>
  );
}
